import * as Tooltip from "@radix-ui/react-tooltip";
import {
  MathJax,
  MathJaxContext,
  type MathJax3Config,
  type MathJaxContextProps,
} from "better-react-mathjax";
import classNames from "classnames/bind";
import React, {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from "react";
import { Icon } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import styles from "./MathDataDisplay.module.scss";
import type {
  MathDataDisplayContextType,
  MathDataDisplayGroupProps,
  MathDataDisplayInteractiveGroupProps,
  MathDataDisplayProps,
  MathDataDisplayReferenceProps,
} from "./types";

const cx = classNames.bind(styles);

const encodings: Record<
  NonNullable<MathDataDisplayContextType["type"]>,
  {
    delimiters: {
      inline: string[][];
      display: string[][];
    };
    isFullyEncoded: (str: string) => boolean;
  }
> = {
  tex: {
    delimiters: {
      inline: [["\\(", "\\)"]],
      display: [
        ["$$", "$$"],
        ["\\[", "\\]"],
      ],
    },
    isFullyEncoded: (str: string) =>
      new RegExp(/^\\\\\((.*)\\\\\)$/).test(str) ||
      new RegExp(/^\\\((.*)\\\)$/).test(str),
  },
  ascii: {
    delimiters: {
      inline: [["`", "`"]],
      display: [["`", "`"]],
    },
    isFullyEncoded: (str: string) => new RegExp(/^`(.*)`$/).test(str),
  },
};

const MathDataDisplayContext = createContext<MathDataDisplayContextType>({});

const useMathDataDisplayContext = () => {
  const ctx = useContext(MathDataDisplayContext);

  if (!ctx) {
    throw new Error("Must be used within a MathDataDisplayRoot component");
  }

  return ctx;
};

/**
 * Renders Math formulas and equations (a math entity) Allows for use of Latex,
 * ASCII Math, or MathML to represent Math.
 * @see {@link https://docs.mathjax.org/en/latest/basic/mathjax.html}
 * @see {@link https://www.npmjs.com/package/better-react-mathjax#example-4-elaborate-example-with-latex}
 */
const MathDataDisplay = ({
  children,
  className,
  style,
  colorScheme = "light",
  isInline: isInlineProp,
  ...restProps
}: MathDataDisplayProps) => {
  const { isInline: isInlineRoot, isLoading: isLoadingRoot } =
    useMathDataDisplayContext();
  const isInline = isInlineProp ?? isInlineRoot;

  return (
    <Skeleton isLoading={isLoadingRoot}>
      <MathJax
        inline={isInline}
        className={cx(
          "container",
          className,
          isInline && "inline",
          colorScheme && `container--color-scheme-${colorScheme}`
        )}
        style={style}
        {...restProps}
      >
        {children}
      </MathJax>
    </Skeleton>
  );
};

const MathDataDisplayRoot = ({
  children,
  type,
  isInline,
  isLoading = false,
  ...restProps
}: PropsWithChildren<
  Extract<MathJaxContextProps, { version?: 3 }> & MathDataDisplayContextType
>) => {
  const defaultConfig: MathJax3Config = {
    loader: {
      load: ["input/asciimath", "output/chtml"],
    },
    "HTML-CSS": {
      linebreaks: { automatic: true },
    },
    SVG: {
      linebreaks: { automatic: true },
    },
    options: {
      enableMenu: true,
      menuOptions: {
        settings: {
          collapsible: false,
          explorer: false,
          zoom: "NoZoom",
          zscale: "150%",
        },
      },
    },
  };

  return (
    <MathDataDisplayContext.Provider value={{ isInline, type, isLoading }}>
      <MathJaxContext
        version={3}
        config={defaultConfig}
        {...restProps}
      >
        {children}
      </MathJaxContext>
    </MathDataDisplayContext.Provider>
  );
};

const MathDataDisplayGroup = ({
  children,
  type: typeProp,
  isInline: isInlineProp,
  isTerm = false,
  className,
  style,
}: MathDataDisplayGroupProps) => {
  const {
    isInline: isInlineRoot,
    type: typeRoot,
    isLoading: isLoadingRoot,
  } = useMathDataDisplayContext();
  const isInline = isInlineProp ?? isInlineRoot;
  const type = typeProp ?? typeRoot;

  const getEncodedMath = (
    eq: string | string[],
    type: MathDataDisplayContextType["type"]
  ) => {
    const actualEq = Array.isArray(eq) ? eq.join("") : eq;

    switch (type) {
      case "tex": {
        if (encodings.tex.isFullyEncoded(actualEq.trim())) return actualEq;

        return `${encodings.tex.delimiters.inline[0][0]}${actualEq}${encodings.tex.delimiters.inline[0][1]}`;
      }
      case "ascii": {
        if (encodings.ascii.isFullyEncoded(actualEq.trim())) return actualEq;

        return `${encodings.ascii.delimiters.inline[0][0]}${actualEq}${encodings.ascii.delimiters.inline[0][1]}`;
      }
    }

    return eq;
  };

  const actualEquation = getEncodedMath(children, type);
  const CustomNode = isInline ? "span" : "div";

  return (
    <Skeleton isLoading={isLoadingRoot}>
      <CustomNode
        className={cx(
          styles.equation,
          className,
          isTerm && "term",
          isInline && "inline"
        )}
        style={style}
      >
        {actualEquation}
      </CustomNode>
    </Skeleton>
  );
};

const MathDataDisplayInteractiveGroup = ({
  children,
  description,
  value,
  displayValue,
  type,
  isInline: isInlineProp,
}: MathDataDisplayInteractiveGroupProps) => {
  const { isInline: isInlineRoot } = useMathDataDisplayContext();
  const [isValueShown, setIsValueShown] = useState(false);

  const isValueValid = typeof value !== "undefined" && value !== null;
  const isInline = isInlineProp ?? isInlineRoot;

  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className={cx("dynamic-term-trigger")}
            onClick={(evt) => {
              evt.stopPropagation();
              setIsValueShown((prev) => !prev);
            }}
            onFocusCapture={(e) => e.stopPropagation()}
            disabled={!isValueValid}
          >
            {isValueShown ? (
              <span
                className={cx(
                  "dynamic-term",
                  "dynamic-term--value-shown",
                  isValueValid && "dynamic-term--value-valid"
                )}
              >
                {displayValue}
              </span>
            ) : null}
            <MathDataDisplayGroup
              isTerm
              isInline={isInline}
              type={type}
              className={cx(
                "dynamic-term",
                isValueValid && "dynamic-term--value-valid"
              )}
              style={isValueShown ? { display: "none" } : {}}
            >
              {children}
            </MathDataDisplayGroup>
          </button>
        </Tooltip.Trigger>
        {description ? (
          <Tooltip.Portal>
            <Tooltip.Content
              className={cx("tooltip-content")}
              sideOffset={5}
              collisionPadding={16}
            >
              {description}
              <Tooltip.Arrow className={cx("tooltip-arrow")} />
            </Tooltip.Content>
          </Tooltip.Portal>
        ) : null}
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

const MathDataDisplayReference = ({
  children,
  className,
  style,
  isInline: isInlineProp,
}: MathDataDisplayReferenceProps) => {
  const { isInline: isInlineRoot, isLoading: isLoadingRoot } =
    useMathDataDisplayContext();

  const isInline = isInlineProp ?? isInlineRoot;
  const CustomNode = isInline ? "span" : "div";

  return (
    <Skeleton isLoading={isLoadingRoot}>
      <CustomNode
        className={cx(styles.reference, className, isInline && "inline")}
        style={style}
      >
        <Icon
          variant="book-open"
          size={16}
        />
        {children}
      </CustomNode>
    </Skeleton>
  );
};

MathDataDisplay.Root = MathDataDisplayRoot;
MathDataDisplay.Reference = MathDataDisplayReference;
MathDataDisplay.Group = MathDataDisplayGroup;
MathDataDisplay.InteractiveGroup = MathDataDisplayInteractiveGroup;

export {
  MathDataDisplay,
  MathDataDisplayGroup,
  MathDataDisplayInteractiveGroup,
  MathDataDisplayReference,
  MathDataDisplayRoot,
};
